/// Pairwise aircraft collision + terrain collision detection.
///
/// Direct port of FlatoutATC/Support/CollisionDetector.swift. Two
/// public functions:
///
///   detectAircraftCollisions(aircrafts, pxPerNM)
///       • crash if horizontal sep < 0.5 NM AND vertical sep < 200 ft
///       • conflict if < 2 NM AND < 500 ft
///       Pairs on the ground (awaitingTakeoff/takingOff) are skipped.
///
///   detectTerrainCollisions(aircrafts, terrain, projector, pxPerNM, airports)
///       • crash if aircraft is inside polygon AND below elevation
///       • warn  if within 2 NM horizontally AND within 300 ft vertically
///       Aircraft inside a 5 NM × 4500 ft "airport buffer" are skipped
///       so approach/departure phases don't trip the detector.

import type { Aircraft } from './Aircraft'
import type { Airport, GeoCoordinate, TerrainObstacle } from '../types/LevelData'
import type { CoordinateProjector } from '../geo/CoordinateProjector'
import { distanceMeters } from '../geo/GeoCoordinate'
import type { Thunderstorm } from './Thunderstorm'

const M_PER_NM = 1852

export type CollisionStatus = 'normal' | 'conflict' | 'terrainWarning' | 'crashed'

export interface AircraftCollision {
  aircraftId1: string
  aircraftId2: string
  status: 'conflict' | 'crashed'
}

export interface TerrainCollision {
  aircraftId: string
  status: 'terrainWarning' | 'crashed'
  /// Elevation of the offending obstacle, in feet.
  terrainElevationFt: number
}

export interface WeatherCollision {
  aircraftId: string
  /// Storm id the aircraft entered. Surfaced for the crash-cause text.
  stormId: string
}

export function detectAircraftCollisions(
  aircrafts: Aircraft[],
  pxPerNM: number,
): AircraftCollision[] {
  const out: AircraftCollision[] = []
  if (aircrafts.length < 2) return out
  for (let i = 0; i < aircrafts.length - 1; i++) {
    for (let j = i + 1; j < aircrafts.length; j++) {
      const a = aircrafts[i]!
      const b = aircrafts[j]!
      if (skipPair(a, b)) continue

      const dx = a.position.x - b.position.x
      const dy = a.position.y - b.position.y
      const horizPx = Math.hypot(dx, dy)
      const horizNM = horizPx / pxPerNM
      const vertFt = Math.abs(a.altitudeFt - b.altitudeFt)

      if (horizNM < 0.5 && vertFt < 200) {
        out.push({ aircraftId1: a.id, aircraftId2: b.id, status: 'crashed' })
      } else if (horizNM < 2.0 && vertFt < 500) {
        out.push({ aircraftId1: a.id, aircraftId2: b.id, status: 'conflict' })
      }
    }
  }
  return out
}

function skipPair(a: Aircraft, b: Aircraft): boolean {
  if (a.status === 'completed' || a.status === 'landed') return true
  if (b.status === 'completed' || b.status === 'landed') return true
  // Departure-flow exception: aircraft in the SAME airport's
  // takeoff sequence (awaiting/takingOff/freshly-departed-and-
  // climbing-out) shouldn't crash with each other. The
  // holding-short aircraft is parked off the active runway in
  // real ops; queued takeoffs and post-takeoff climbouts
  // overflying the threshold are normal flow.
  //
  // The discriminator below uses `departureRunway`, which is
  // ONLY set on aircraft spawned for a takeoff/departure goal.
  // Landing aircraft (status `landing`) and inbound transitions
  // don't have it, so they fall through to the standard collision
  // check — a landing aircraft passing through a holding-short
  // aircraft IS a runway incursion and DOES crash, which matches
  // both ATC reality and player expectation.
  const aDep = departureSequenceAirport(a)
  const bDep = departureSequenceAirport(b)
  if (aDep != null && bDep != null && aDep === bDep) return true
  return false
}

/// Returns the departure-airport name for aircraft that are
/// currently in the takeoff sequence (holding short, rolling, or
/// freshly enRoute after a departure-goal launch). Returns null
/// for landing/transition/arrival aircraft and for any state
/// where collision rules should apply normally.
function departureSequenceAirport(aircraft: Aircraft): string | null {
  if (!aircraft.departureRunway) return null
  if (
    aircraft.status === 'awaitingTakeoff' ||
    aircraft.status === 'takingOff' ||
    aircraft.status === 'enRoute'
  ) {
    return aircraft.departureRunway.airportName
  }
  return null
}

const AIRPORT_BUFFER_NM = 5
const AIRPORT_BUFFER_ALT_FT = 4500

export function detectTerrainCollisions(args: {
  aircrafts: Aircraft[]
  terrain: TerrainObstacle[]
  airports: Airport[]
  projector: CoordinateProjector
  pxPerNM: number
}): TerrainCollision[] {
  const { aircrafts, terrain, airports, projector, pxPerNM } = args
  const out: TerrainCollision[] = []
  if (terrain.length === 0) return out

  for (const aircraft of aircrafts) {
    if (
      aircraft.status === 'awaitingTakeoff' ||
      aircraft.status === 'takingOff' ||
      aircraft.status === 'completed' ||
      aircraft.status === 'landed'
    ) {
      continue
    }

    const aircraftGeo = unprojectAircraft(aircraft, projector, pxPerNM, airports)
    if (!aircraftGeo) continue

    if (isInAirportBuffer(aircraft, aircraftGeo, airports)) continue

    for (const obstacle of terrain) {
      const inside = pointInPolygon(aircraftGeo, obstacle.coordinates)
      if (inside && aircraft.altitudeFt < obstacle.elevationInFeet) {
        out.push({
          aircraftId: aircraft.id,
          status: 'crashed',
          terrainElevationFt: obstacle.elevationInFeet,
        })
        break
      }
      const distM = distanceToPolygonEdge(aircraftGeo, obstacle.coordinates)
      const distNM = distM / M_PER_NM
      const vertFt = Math.abs(aircraft.altitudeFt - obstacle.elevationInFeet)
      if (
        distNM < 2 &&
        vertFt < 300 &&
        aircraft.altitudeFt > obstacle.elevationInFeet
      ) {
        out.push({
          aircraftId: aircraft.id,
          status: 'terrainWarning',
          terrainElevationFt: obstacle.elevationInFeet,
        })
        break
      }
    }
  }
  return out
}

/// Convert the aircraft's world-pixel position back to lat/lon.
/// Cheap inverse of CoordinateProjector.project() — same constants
/// but in reverse.
function unprojectAircraft(
  aircraft: Aircraft,
  projector: CoordinateProjector,
  _pxPerNM: number,
  _airports: Airport[],
): GeoCoordinate | null {
  // We don't have a public unproject on the projector yet — derive
  // by binary-search-free inverse: the projector is a linear function
  // of (lat, lon) with magvar rotation, so we can solve algebraically.
  // To keep this commit small we approximate by sampling: project the
  // first airport and the aircraft, then add the lat/lon delta scaled
  // by projector linearity. Good enough — the only consumer is point-
  // in-polygon, which only needs sub-NM accuracy.
  return projectorInverse(projector, aircraft.position)
}

/// Reverses the projector by reading its private fields via the
/// public API: project a known coord, project the aircraft, and
/// linearly interpolate. Stable because the projection is linear.
function projectorInverse(
  projector: CoordinateProjector,
  point: { x: number; y: number },
): GeoCoordinate {
  // Sample two reference points (1 deg apart) to build a local
  // affine transform from world-pixel back to lat/lon.
  const ref0 = projector.project({ latitude: 0, longitude: 0 }, true)
  const refLat = projector.project({ latitude: 1, longitude: 0 }, true)
  const refLon = projector.project({ latitude: 0, longitude: 1 }, true)

  const dxLat = refLat.x - ref0.x
  const dyLat = refLat.y - ref0.y
  const dxLon = refLon.x - ref0.x
  const dyLon = refLon.y - ref0.y

  const px = point.x - ref0.x
  const py = point.y - ref0.y

  // Solve [dxLat dxLon; dyLat dyLon] · [lat lon]^T = [px py]^T
  const det = dxLat * dyLon - dxLon * dyLat
  if (Math.abs(det) < 1e-9) return { latitude: 0, longitude: 0 }
  const latitude = (px * dyLon - py * dxLon) / det
  const longitude = (py * dxLat - px * dyLat) / det
  return { latitude, longitude }
}

function isInAirportBuffer(
  aircraft: Aircraft,
  aircraftGeo: GeoCoordinate,
  airports: Airport[],
): boolean {
  for (const airport of airports) {
    const distM = distanceMeters(aircraftGeo, airport.centerCoordinate)
    const distNM = distM / M_PER_NM
    const elev = airport.elevation ?? 0
    if (
      distNM <= AIRPORT_BUFFER_NM &&
      Math.abs(aircraft.altitudeFt - elev) <= AIRPORT_BUFFER_ALT_FT
    ) {
      return true
    }
  }
  return false
}

/// Ray-casting point-in-polygon. Fast, returns true for points
/// inside the closed polygon (winding-rule independent).
function pointInPolygon(point: GeoCoordinate, ring: GeoCoordinate[]): boolean {
  let inside = false
  const x = point.longitude
  const y = point.latitude
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i]!.longitude
    const yi = ring[i]!.latitude
    const xj = ring[j]!.longitude
    const yj = ring[j]!.latitude
    const intersect =
      (yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside
  }
  return inside
}

/// Min distance (in meters) from `point` to the polygon's boundary.
function distanceToPolygonEdge(point: GeoCoordinate, ring: GeoCoordinate[]): number {
  let min = Infinity
  for (let i = 0; i < ring.length; i++) {
    const j = (i + 1) % ring.length
    const d = distanceToSegment(point, ring[i]!, ring[j]!)
    if (d < min) min = d
  }
  return min
}

function distanceToSegment(
  p: GeoCoordinate,
  a: GeoCoordinate,
  b: GeoCoordinate,
): number {
  const dx = b.longitude - a.longitude
  const dy = b.latitude - a.latitude
  if (Math.abs(dx) < 1e-12 && Math.abs(dy) < 1e-12) {
    return distanceMeters(p, a)
  }
  const t = Math.max(
    0,
    Math.min(
      1,
      ((p.longitude - a.longitude) * dx + (p.latitude - a.latitude) * dy) /
        (dx * dx + dy * dy),
    ),
  )
  const nearest: GeoCoordinate = {
    latitude: a.latitude + t * dy,
    longitude: a.longitude + t * dx,
  }
  return distanceMeters(p, nearest)
}

/// Aircraft-vs-thunderstorm sweep. Centroid-to-centroid distance ≤
/// storm radius ⟹ the aircraft is "in the cell" and is lost. Skips
/// aircraft on the ground (no horizontal exposure) and already-crashed
/// or completed traffic. Mirrors iOS GameViewModel.swift:3846-3868.
export function detectWeatherCollisions(args: {
  aircrafts: readonly Aircraft[]
  storms: readonly Thunderstorm[]
  pxPerNM: number
}): WeatherCollision[] {
  const out: WeatherCollision[] = []
  if (args.storms.length === 0) return out
  for (const aircraft of args.aircrafts) {
    if (
      aircraft.collisionStatus === 'crashed' ||
      aircraft.status === 'awaitingTakeoff' ||
      aircraft.status === 'completed' ||
      aircraft.status === 'landed'
    ) {
      continue
    }
    for (const storm of args.storms) {
      const radiusPx = storm.sizeNM * args.pxPerNM
      const dx = aircraft.position.x - storm.x
      const dy = aircraft.position.y - storm.y
      if (Math.hypot(dx, dy) <= radiusPx) {
        out.push({ aircraftId: aircraft.id, stormId: storm.id })
        break
      }
    }
  }
  return out
}

/// Returns true if any storm covers the given world-space position
/// (e.g., a runway threshold). Used to refuse takeoff clearance and
/// to inhibit auto-promotion of queued departures while a cell is
/// parked over the runway. Mirrors iOS isThunderstormOverRunway.
export function isPositionInStorm(args: {
  position: { x: number; y: number }
  storms: readonly Thunderstorm[]
  pxPerNM: number
}): boolean {
  for (const storm of args.storms) {
    const radiusPx = storm.sizeNM * args.pxPerNM
    const dx = args.position.x - storm.x
    const dy = args.position.y - storm.y
    if (Math.hypot(dx, dy) <= radiusPx) return true
  }
  return false
}
